import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

export const MADRID_TIME_ZONE = 'Europe/Madrid';

export function resolvePositiveNumber(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback;
}

export function resolveNumber(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function formateaFechaSimple(fechaStr: string): string {
  const fecha = new Date(fechaStr.replace(' ', 'T'));
  const dia = String(fecha.getDate()).padStart(2, '0');
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const year = fecha.getFullYear();
  return `${dia}/${mes}/${year}`;
}

export function formatPartidoLiteral(name: string, date: string): string {
  return `${name} - ${formateaFechaSimple(date)}`;
}

export function formatTime(seconds: number): string {
  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export function parseBackendDate(dateString: string, timeZone: string = MADRID_TIME_ZONE): number {
  const normalized = dateString.replace(' ', 'T');
  if (hasExplicitTimezone(normalized)) {
    return new Date(limitMillisecondsPrecision(normalized)).getTime();
  }
  // Sin zona explícita: lo tratamos como fecha/hora local del navegador.
  return new Date(limitMillisecondsPrecision(normalized)).getTime();
}

export function getMadridDateNow(): number {
  return Date.now();
}

export function normalizeToMadridUtc(date: Date, assumeMadridLocal: boolean, timeZone: string = MADRID_TIME_ZONE): number {
  if (!assumeMadridLocal) {
    return date.getTime();
  }

  return date.getTime() - getTimeZoneOffsetMs(timeZone, date);
}

export function getTimeZoneOffsetMs(timeZone: string, date = new Date()): number {
  const utcString = date.toLocaleString('en-US', { timeZone: 'UTC' });
  const tzString = date.toLocaleString('en-US', { timeZone });
  return new Date(tzString).getTime() - new Date(utcString).getTime();
}

function hasExplicitTimezone(dateString: string): boolean {
  return /([zZ]|[+-]\d{2}:?\d{2})$/.test(dateString);
}

function limitMillisecondsPrecision(dateString: string): string {
  return dateString.replace(/(\.\d{3})\d*/, '$1');
}

const DNI_NIE_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';

export function normalizeWhitespace(value: unknown): string {
  return String(value ?? '').trim().replace(/\s+/g, ' ');
}

export function normalizeEmail(value: unknown): string {
  return normalizeWhitespace(value).toLowerCase();
}

export function normalizeDni(value: unknown): string {
  return normalizeWhitespace(value).toUpperCase().replace(/[\s-]/g, '');
}

export function normalizePhone(value: unknown): string {
  const trimmed = normalizeWhitespace(value);
  if (!trimmed) return '';

  if (!/^\+?[\d\s().-]+$/.test(trimmed)) {
    return trimmed;
  }

  let normalized = trimmed.replace(/[()\s.-]/g, '');

  if (normalized.startsWith('00')) {
    normalized = `+${normalized.slice(2)}`;
  }

  if (normalized.startsWith('+')) {
    const digits = normalized.slice(1).replace(/\D/g, '');
    return `+${digits}`;
  }

  return normalized.replace(/\D/g, '');
}

function isValidDni(value: string): boolean {
  if (!/^\d{8}[A-Z]$/.test(value)) return false;
  const number = Number(value.slice(0, 8));
  const letter = value[8];
  return letter === DNI_NIE_LETTERS[number % 23];
}

function isValidNie(value: string): boolean {
  if (!/^[XYZ]\d{7}[A-Z]$/.test(value)) return false;
  const numeric = value.replace('X', '0').replace('Y', '1').replace('Z', '2');
  const number = Number(numeric.slice(0, 8));
  const letter = value[8];
  return letter === DNI_NIE_LETTERS[number % 23];
}

function isValidForeignDocument(value: string): boolean {
  if (!/^[A-Z0-9][A-Z0-9/.-]{4,19}$/.test(value)) return false;
  // Evita aceptar cadenas puramente numericas como documento extranjero.
  return /[A-Z]/.test(value);
}

function isValidPhone(value: string): boolean {
  if (!value) return false;
  if (!/^\+?\d+$/.test(value)) return false;

  const digits = value.startsWith('+') ? value.slice(1) : value;
  if (digits.length < 9 || digits.length > 15) return false;

  // Con prefijo internacional E.164 no puede empezar por 0.
  if (value.startsWith('+') && /^0/.test(digits)) return false;
  return true;
}

export const telefonoValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) return null;
  const normalized = normalizePhone(control.value);
  return isValidPhone(normalized) ? null : { telefonoInvalido: true };
};

export const dniValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) return null;
  const value = normalizeDni(control.value);

  if (isValidDni(value) || isValidNie(value) || isValidForeignDocument(value)) {
    return null;
  }

  return { dniInvalido: true };
};

export const codigoPostalValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (!value) return null;
  const valid = /^(0[1-9]|[1-4]\d|5[0-2])\d{3}$/.test(value.trim());
  return valid ? null : { codigoPostal: true };
}
